import { Box, NavbarSearchBox, Theme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { searchPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { useMobileSearchBar } from "@outschool/ui-components-website";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router-dom";

import { useActiveLearnerSearchFilters } from "../../hooks/useActiveLearnerSearchFilters";
import useExperiment from "../../hooks/useExperiment";
import { SearchFiltersContextProvider } from "../../lib/useSearchFilters";
import { useSearchByLearner } from "../../providers/SearchByLearnerProvider";
import DesktopSearchField from "../search/desktop/DesktopSearchField";

function useGlobalSearchTeachersExperiment() {
  const { isLoggedIn } = useSession();
  const { variant, trigger } = useExperiment("GlobalSearchTeachers");
  const isTreatment = isLoggedIn && variant === "treatment";
  return {
    isTreatment,
    trigger
  };
}

export default function GlobalSearchBar({
  showSearchBox
}: {
  showSearchBox: boolean;
}) {
  const nav = useNavigation();
  const { isTreatment, trigger } = useGlobalSearchTeachersExperiment();

  const getLearnerSearchFilters = useActiveLearnerSearchFilters();
  const { learner, setLearner, getSearchBarPlaceholder } = useSearchByLearner();

  return (
    <Box
      sx={(theme: Theme) => ({
        width: "100%",
        [theme.breakpoints.down("md")]: {
          display: "none"
        },
        // for Pad Mini 768px
        "@media (min-width: 768px) and (max-width: 768px)": {
          "* > form": {
            padding: 0
          }
        }
      })}
    >
      {showSearchBox ? (
        <SearchFiltersContextProvider>
          <DesktopSearchField
            searchFilters={{}}
            setImplicitSearchFilters={() => {}}
            setSearchFilters={filters => {
              if (learner && hasQueryFilterOnly(filters)) {
                // should update 'search by learner' state only when the clicked keyword does not contain other filters
                const learnerFilters = getLearnerSearchFilters(learner);
                filters = { ...filters, ...learnerFilters };
              } else {
                // should reset 'search by learner' state when the clicked keyword contains other filters
                setLearner(null);
              }
              nav(searchPath(filters));
            }}
            clearDropdowns={() => {}}
            /* eslint-disable i18next/no-literal-string */
            searchSuggestionsToIgnore={isTreatment ? [] : ["teachers"]}
            experimentTrigger={trigger}
            showSearchSuggestions
            SearchBoxComponent={NavbarSearchBox}
            searchPlaceholder={getSearchBarPlaceholder(learner)}
            menuStyleSx={{
              top: "45px"
            }}
          />
        </SearchFiltersContextProvider>
      ) : (
        // simple box to keep header sizing the same with/without search
        <Box sx={{ height: 55 }} />
      )}
    </Box>
  );
}

export function MobileSearchBar() {
  const ref = React.useRef<HTMLElement>(null);
  const { showMobileSearchBar, setVerticalOffset } = useMobileSearchBar();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isOnIgnoredPage = ["/search", "/users/login"].includes(
    location.pathname
  );

  React.useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const { height } = ref.current.getBoundingClientRect();

    setVerticalOffset(height);
  }, [setVerticalOffset]);
  if (!isMobile || isOnIgnoredPage || !showMobileSearchBar) {
    return null;
  }

  return (
    <Box
      ref={ref}
      sx={(theme: Theme) => ({
        width: "100%",
        position: "fixed",
        padding: "5px 10px",
        top: 0,
        left: 0,
        zIndex: 2,
        backgroundColor: theme.palette.common.white,
        borderBottom: `1px solid ${theme.palette.grey[200]}`
      })}
    >
      <SearchBar />
    </Box>
  );
}

export function SearchBar() {
  const nav = useNavigation();
  const { t } = useTranslation("client\\components\\nav\\GlobalSearchBar");
  return (
    <SearchFiltersContextProvider>
      <DesktopSearchField
        searchFilters={{}}
        setImplicitSearchFilters={() => {}}
        setSearchFilters={filters => {
          nav(searchPath(filters));
        }}
        clearDropdowns={() => {}}
        /* eslint-disable i18next/no-literal-string */
        showSearchSuggestions
        SearchBoxComponent={NavbarSearchBox}
        searchPlaceholder={t("Search online classes and tutors")}
        menuStyleSx={{
          top: "45px"
        }}
      />
    </SearchFiltersContextProvider>
  );
}

function hasQueryFilterOnly(filters: any): boolean {
  if (!filters || typeof filters !== "object") {
    return false;
  }

  return Object.entries(filters).every(([key, value]) => {
    if (key === "q") {
      return true;
    }
    return value === null || value === undefined;
  });
}
